import { parseServerMessage, RealtimeCloseCode, RealtimeMessageType, type RealtimeEvent, type RealtimePong, type RealtimeSubscribe, type RealtimeWatchOnline } from '@gr/core/platform/system/realtime';
import { UI_DEFAULTS, type UiConfig } from '@gr/core/platform/config/ui-config';
import { Observable } from '../store';

// Соединение событий на уровне приложения (SYS-UC-13, ADR-012): одно на вкладку, само
// переподключается, повторяет подписку и сообщает, когда связи нет дольше пары секунд.

export enum RealtimeStatus {
  /** Не нужно: пользователь не вошёл. */
  Idle = 'idle',
  Connecting = 'connecting',
  Open = 'open',
  /** Не установилось или оборвалось — ждём следующей попытки. */
  Down = 'down',
}

/** То, что нужно от браузерного WebSocket, — чтобы тесты подставляли свой. */
export interface SocketLike {
  onopen: (() => void) | null;
  onclose: ((event: { code: number }) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  send(text: string): void;
  close(): void;
}

export type SocketFactory = (url: string) => SocketLike;

export interface Scheduler {
  setTimeout(run: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

/**
 * SYS-UC-13.CC-1 (конфигурация ui.realtime): паузы между попытками, дальше — последняя; панель — только
 * если связи нет дольше problemAfterMs; связь восстановлена, только если соединение продержалось
 * stableAfterMs — прокси, который принимает и тут же рвёт соединение, это тоже «не удаётся».
 */
export type RealtimeSettings = UiConfig['realtime'];

const browserScheduler: Scheduler = {
  setTimeout: (run, ms) => globalThis.setTimeout(run, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>),
};

export class RealtimeConnection {
  readonly status = new Observable<RealtimeStatus>(RealtimeStatus.Idle);
  /** Связи нет дольше problemAfterMs — показывать панель (SYS-UC-13.CC-1). */
  readonly problem = new Observable<boolean>(false);
  /** Сервер закрыл соединение из-за сессии (SYS-UC-13.CC-2) — приложение выходит. */
  onUnauthorized: () => void = () => {};

  private socket: SocketLike | null = null;
  private running = false;
  private failures = 0;
  private retryTimer: unknown = null;
  private problemTimer: unknown = null;
  private stableTimer: unknown = null;
  private blogId: string | null = null;
  private watchingOnline = false;
  private readonly listeners = new Set<(message: RealtimeEvent) => void>();

  constructor(
    private readonly url: string,
    private readonly createSocket: SocketFactory,
    private readonly scheduler: Scheduler = browserScheduler,
    /** Читается при каждой попытке: изменённая конфигурация действует без перезагрузки. */
    private readonly settings: () => RealtimeSettings = () => UI_DEFAULTS.realtime,
  ) {}

  /** Пользователь вошёл: подключиться и держать связь. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.connect();
  }

  /** Пользователь вышел: закрыть и больше не переподключаться. */
  stop(): void {
    this.running = false;
    this.cancel('retryTimer');
    this.cancel('problemTimer');
    this.cancel('stableTimer');
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.failures = 0;
    this.status.set(RealtimeStatus.Idle);
    this.problem.set(false);
  }

  /** «Переподключить», возвращение сети или вкладки: не ждать паузу. */
  reconnectNow(): void {
    if (!this.running || this.status.get() !== RealtimeStatus.Down) return;
    this.cancel('retryTimer');
    this.connect();
  }

  /** За каким блогом следить; запоминается и повторяется после переподключения. */
  subscribe(blogId: string | null): void {
    this.blogId = blogId;
    this.sendSubscription();
  }

  /** ADM-UC-3: следить ли за «Онлайн»; запоминается и повторяется после переподключения. */
  watchOnline(watch: boolean): void {
    this.watchingOnline = watch;
    this.sendWatchOnline();
  }

  onMessage(listener: (message: RealtimeEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private connect(): void {
    this.status.set(RealtimeStatus.Connecting);
    this.armProblem();
    let socket: SocketLike;
    try {
      socket = this.createSocket(this.url);
    } catch {
      this.lost();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.status.set(RealtimeStatus.Open);
      this.sendSubscription();
      // Отписка серверу после переподключения не нужна: новое соединение ни за чем не следит.
      if (this.watchingOnline) this.sendWatchOnline();
      this.stableTimer = this.scheduler.setTimeout(() => {
        this.stableTimer = null;
        this.failures = 0;
        this.cancel('problemTimer');
        this.problem.set(false);
      }, this.settings().stableAfterMs);
    };
    socket.onmessage = (event) => {
      const message = parseServerMessage(String(event.data));
      if (!message) return;
      // SYS-UC-13.CC-2: проверка живости — ответить сразу, подписчикам она не нужна.
      if (message.type === RealtimeMessageType.Ping) {
        const pong: RealtimePong = { type: RealtimeMessageType.Pong };
        socket.send(JSON.stringify(pong));
        return;
      }
      for (const listener of this.listeners) listener(message);
    };
    socket.onclose = (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.cancel('stableTimer');
      if (event.code === RealtimeCloseCode.Unauthorized) {
        this.stop();
        this.onUnauthorized();
        return;
      }
      this.lost();
    };
  }

  private lost(): void {
    if (!this.running) return;
    this.status.set(RealtimeStatus.Down);
    this.armProblem();
    const delays = this.settings().reconnectDelaysMs;
    const delay = delays[Math.min(this.failures, delays.length - 1)]!;
    this.failures += 1;
    this.retryTimer = this.scheduler.setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  private sendWatchOnline(): void {
    if (this.status.get() !== RealtimeStatus.Open || !this.socket) return;
    const message: RealtimeWatchOnline = { type: RealtimeMessageType.WatchOnline, watch: this.watchingOnline };
    this.socket.send(JSON.stringify(message));
  }

  private sendSubscription(): void {
    if (this.status.get() !== RealtimeStatus.Open || !this.socket) return;
    const message: RealtimeSubscribe = { type: RealtimeMessageType.Subscribe, blogId: this.blogId };
    this.socket.send(JSON.stringify(message));
  }

  private armProblem(): void {
    if (this.problemTimer !== null || this.problem.get()) return;
    this.problemTimer = this.scheduler.setTimeout(() => {
      this.problemTimer = null;
      if (this.running && this.status.get() !== RealtimeStatus.Open) this.problem.set(true);
    }, this.settings().problemAfterMs);
  }

  private cancel(timer: 'retryTimer' | 'problemTimer' | 'stableTimer'): void {
    if (this[timer] === null) return;
    this.scheduler.clearTimeout(this[timer]);
    this[timer] = null;
  }
}
